// ============================================================
// supabaseService.js - Ponte tra SafeTracker App e Supabase DB
// ============================================================
// Questo file gestisce TUTTE le operazioni con il database.
// Traduce i nomi dei campi: App (camelCase) ↔ DB (snake_case)
// ============================================================

import { supabase } from './supabaseClient';

// ============================================================
// HELPER: Conversione nomi campi
// ============================================================

// --- COMPANIES ---
const companyToDb = (company) => ({
  ragione_sociale: company.ragioneSociale,
  partita_iva: company.partitaIva,
  codice_fiscale: company.codiceFiscale || '',
  pec: company.pec,
  email: company.email || '',
  telefono: company.telefono || '',
  indirizzo: company.indirizzo,
  cap: company.cap || '',
  citta: company.citta,
  provincia: company.provincia || '',
  tipo_contratto: company.tipoContratto,
  attiva: company.attiva,
  data_inizio_contratto: company.dataInizioContratto || null,
  data_scadenza_contratto: company.dataScadenzaContratto || null,
  importo_contratto: company.importoContratto || null,
  note_contratto: company.noteContratto || '',
  logo_url: company.logo || null,
  datore_lavoro: company.datoreLavoro || {},
  medico_competente: company.medicoCompetente || {},
  rspp: company.rspp || {},
  codici_ateco: company.codiciAteco || [],
});

const companyFromDb = (row) => ({
  id: row.id,
  ragioneSociale: row.ragione_sociale || '',
  partitaIva: row.partita_iva || '',
  codiceFiscale: row.codice_fiscale || '',
  pec: row.pec || '',
  email: row.email || '',
  telefono: row.telefono || '',
  indirizzo: row.indirizzo || '',
  cap: row.cap || '',
  citta: row.citta || '',
  provincia: row.provincia || '',
  tipoContratto: row.tipo_contratto || 'gestione_completa',
  attiva: row.attiva !== false,
  dataInizioContratto: row.data_inizio_contratto || '',
  dataScadenzaContratto: row.data_scadenza_contratto || '',
  importoContratto: row.importo_contratto || 0,
  noteContratto: row.note_contratto || '',
  logo: row.logo_url || null,
  datoreLavoro: row.datore_lavoro || { nome: '', cognome: '', email: '', cellulare: '', cumulaRuoli: false, ruoliCumulati: [] },
  medicoCompetente: row.medico_competente || { nome: '', cognome: '', email: '', pec: '', cellulare: '' },
  rspp: row.rspp || { nome: '', cognome: '', email: '', pec: '', cellulare: '' },
  codiciAteco: row.codici_ateco || [],
});

// --- WORKERS ---
const workerToDb = (worker) => ({
  company_id: worker.companyId,
  nome: worker.nome,
  cognome: worker.cognome,
  codice_fiscale: worker.codiceFiscale || '',
  email: worker.email || '',
  cellulare: worker.cellulare || '',
  data_nascita: worker.dataNascita || null,
  luogo_nascita: worker.luogoNascita || '',
  indirizzo: worker.indirizzo || '',
  cap: worker.cap || '',
  citta: worker.citta || '',
  provincia: worker.provincia || '',
  mansione: worker.mansione || '',
  ruoli: worker.ruoli || [],
  data_assunzione: worker.dataAssunzione || null,
  data_fine: worker.dataFine || null,
  attivo: worker.attivo !== false,
  note: worker.note || '',
  storico_eventi: worker.storicoEventi || [],
});

const workerFromDb = (row) => ({
  id: row.id,
  companyId: row.company_id,
  nome: row.nome || '',
  cognome: row.cognome || '',
  codiceFiscale: row.codice_fiscale || '',
  email: row.email || '',
  cellulare: row.cellulare || '',
  dataNascita: row.data_nascita || '',
  luogoNascita: row.luogo_nascita || '',
  indirizzo: row.indirizzo || '',
  cap: row.cap || '',
  citta: row.citta || '',
  provincia: row.provincia || '',
  mansione: row.mansione || '',
  ruoli: row.ruoli || [],
  dataAssunzione: row.data_assunzione || '',
  dataFine: row.data_fine || '',
  attivo: row.attivo !== false,
  note: row.note || '',
  storicoEventi: row.storico_eventi || [],
});

// --- COMMUNICATIONS ---
const communicationToDb = (comm) => ({
  company_id: comm.aziendaId,
  titolo: comm.titolo,
  messaggio: comm.contenuto,
  tipologia: comm.tipologia,
  stato: comm.stato,
  destinatari_tipo: comm.destinatari?.tuttiLavoratori ? 'tutti' : 'filtro',
  destinatari_filtro: comm.destinatari || {},
  destinatari_ids: comm.recipients?.filter(r => !String(r.id).startsWith('org-')).map(r => r.id) || [],
  data_invio: comm.dataInvio || null,
  data_scadenza: comm.dataScadenza || null,
  richiede_conferma: comm.richiedeConferma || false,
  allegati: comm.allegati || null,
  hash_sha256: comm.hashSha256 || null,
  created_by: comm.createdBy || null,
});

const communicationFromDb = (row, reads = []) => ({
  id: row.id,
  titolo: row.titolo || '',
  contenuto: row.messaggio || '',
  tipologia: row.tipologia || 'Messaggio',
  stato: row.stato || 'Bozza',
  aziendaId: row.company_id,
  dataInvio: row.data_invio || '',
  dataScadenza: row.data_scadenza || '',
  richiedeConferma: row.richiede_conferma || false,
  allegati: row.allegati || null,
  hashSha256: row.hash_sha256 || '',
  createdBy: row.created_by || null,
  destinatari: row.destinatari_filtro || {
    mansioni: [],
    ruoli: [],
    ruoliOrganizzativi: [],
    tuttiLavoratori: false,
    personeSpecifiche: []
  },
  // Recipients vengono dalla tabella communication_reads
  recipients: reads.map(r => ({
    id: r.worker_id,
    nome: r.worker_nome || '',
    letto: r.letto || false,
    dataLettura: r.data_lettura || null,
    solleciti: r.solleciti || [],
  })),
});

// --- DOCUMENTS ---
const documentToDb = (doc) => ({
  company_id: doc.companyId,
  tipo: doc.tipo,
  descrizione: doc.descrizione,
  file_url: doc.fileUrl || null,
  file_name: doc.file || null,
  file_size: doc.fileSize || null,
  mime_type: doc.mimeType || null,
  data_documento: doc.dataCaricamento ? doc.dataCaricamento.split('T')[0] : null,
  data_revisione: doc.dataRevisione || null,
  data_scadenza: doc.dataScadenza || null,
  stato: doc.stato,
  versione: doc.version || 1,
  richiede_approvazione: doc.richiedeApprovazione || false,
  destinatari_tipo: doc.destinatari?.tuttiLavoratori ? 'tutti' : 'filtro',
  destinatari_ids: doc.recipients?.filter(r => !String(r.id).startsWith('org-')).map(r => r.id) || [],
  hash_sha256: doc.hashSha256 || null,
  revisioni: doc.revisioni || null,
  created_by: doc.createdBy || null,
  storage_path: doc.storagePath || null,
});

const documentFromDb = (row, shares = []) => ({
  id: row.id,
  companyId: row.company_id,
  tipo: row.tipo || '',
  descrizione: row.descrizione || '',
  fileUrl: row.file_url || null,
  file: row.file_name || '',
  fileSize: row.file_size || null,
  mimeType: row.mime_type || null,
  dataCaricamento: row.created_at || '',
  dataRevisione: row.data_revisione || '',
  dataScadenza: row.data_scadenza || '',
  stato: row.stato || 'Privato',
  version: row.versione || 1,
  replacedDocumentId: null, // Gestito via revisioni
  isArchived: row.stato === 'Archiviato',
  richiedeApprovazione: row.richiede_approvazione || false,
  hashSha256: row.hash_sha256 || '',
  revisioni: row.revisioni || null,
  createdBy: row.created_by || null,
  storagePath: row.storage_path || null,
  destinatari: {
    mansioni: [],
    ruoli: [],
    tuttiLavoratori: row.destinatari_tipo === 'tutti',
    personeSpecifiche: []
  },
  // Recipients vengono dalla tabella document_shares
  recipients: shares.map(s => ({
    id: s.worker_id,
    nome: s.worker_nome || '',
    letto: s.letto || false,
    dataLettura: s.data_lettura || null,
    approvato: s.approvato || false,
    dataApprovazione: s.data_approvazione || null,
  })),
});


// ============================================================
// COMPANIES - Operazioni CRUD
// ============================================================

export const companiesService = {
  // Leggi tutte le aziende
  async getAll() {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .order('ragione_sociale');
    
    if (error) {
      console.error('Errore lettura aziende:', error);
      throw error;
    }
    return (data || []).map(companyFromDb);
  },

  // Leggi una singola azienda
  async getById(id) {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error('Errore lettura azienda:', error);
      throw error;
    }
    return companyFromDb(data);
  },

  // Crea nuova azienda
  async create(company) {
    const dbData = companyToDb(company);
    const { data, error } = await supabase
      .from('companies')
      .insert(dbData)
      .select()
      .single();
    
    if (error) {
      console.error('Errore creazione azienda:', error);
      throw error;
    }
    return companyFromDb(data);
  },

  // Aggiorna azienda
  async update(id, company) {
    const dbData = companyToDb(company);
    const { data, error } = await supabase
      .from('companies')
      .update({ ...dbData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore aggiornamento azienda:', error);
      throw error;
    }
    return companyFromDb(data);
  },

  // Elimina azienda (soft delete - disattiva)
  async deactivate(id) {
    const { data, error } = await supabase
      .from('companies')
      .update({ attiva: false, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore disattivazione azienda:', error);
      throw error;
    }
    return companyFromDb(data);
  },

  // Riattiva azienda
  async reactivate(id) {
    const { data, error } = await supabase
      .from('companies')
      .update({ attiva: true, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore riattivazione azienda:', error);
      throw error;
    }
    return companyFromDb(data);
  },
};


// ============================================================
// WORKERS - Operazioni CRUD
// ============================================================

export const workersService = {
  // Leggi tutti i lavoratori
  async getAll() {
    const { data, error } = await supabase
      .from('workers')
      .select('*')
      .order('cognome');
    
    if (error) {
      console.error('Errore lettura lavoratori:', error);
      throw error;
    }
    return (data || []).map(workerFromDb);
  },

  // Leggi lavoratori di una specifica azienda
  async getByCompany(companyId) {
    const { data, error } = await supabase
      .from('workers')
      .select('*')
      .eq('company_id', companyId)
      .order('cognome');
    
    if (error) {
      console.error('Errore lettura lavoratori azienda:', error);
      throw error;
    }
    return (data || []).map(workerFromDb);
  },

  // Crea nuovo lavoratore
  async create(worker) {
    const dbData = workerToDb(worker);
    const { data, error } = await supabase
      .from('workers')
      .insert(dbData)
      .select()
      .single();
    
    if (error) {
      console.error('Errore creazione lavoratore:', error);
      throw error;
    }
    return workerFromDb(data);
  },

  // Crea più lavoratori (import bulk)
  async createMany(workersArray) {
    const dbDataArray = workersArray.map(workerToDb);
    const { data, error } = await supabase
      .from('workers')
      .insert(dbDataArray)
      .select();
    
    if (error) {
      console.error('Errore import lavoratori:', error);
      throw error;
    }
    return (data || []).map(workerFromDb);
  },

  // Aggiorna lavoratore
  async update(id, worker) {
    const dbData = workerToDb(worker);
    const { data, error } = await supabase
      .from('workers')
      .update({ ...dbData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore aggiornamento lavoratore:', error);
      throw error;
    }
    return workerFromDb(data);
  },

  // Dismissione lavoratore
  async dismiss(id, dataFine) {
    const { data, error } = await supabase
      .from('workers')
      .update({ 
        data_fine: dataFine, 
        attivo: false,
        updated_at: new Date().toISOString() 
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore dismissione lavoratore:', error);
      throw error;
    }
    return workerFromDb(data);
  },

  // Elimina lavoratore (hard delete)
  async delete(id) {
    const { error } = await supabase
      .from('workers')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Errore eliminazione lavoratore:', error);
      throw error;
    }
    return true;
  },
};


// ============================================================
// COMMUNICATIONS - Operazioni CRUD
// ============================================================

export const communicationsService = {
  // Leggi tutte le comunicazioni con i recipients
  async getAll() {
    // 1. Leggi comunicazioni
    const { data: comms, error: commError } = await supabase
      .from('communications')
      .select('*')
      .order('data_invio', { ascending: false });
    
    if (commError) {
      console.error('Errore lettura comunicazioni:', commError);
      throw commError;
    }

    // 2. Leggi tutte le letture
    const { data: reads, error: readError } = await supabase
      .from('communication_reads')
      .select('*');
    
    if (readError) {
      console.error('Errore lettura conferme:', readError);
      throw readError;
    }

    // 3. Leggi tutti i lavoratori per i nomi
    const { data: allWorkers, error: workerError } = await supabase
      .from('workers')
      .select('id, nome, cognome');
    
    if (workerError) {
      console.error('Errore lettura nomi lavoratori:', workerError);
      throw workerError;
    }

    // Mappa nomi lavoratori
    const workerNames = {};
    (allWorkers || []).forEach(w => {
      workerNames[w.id] = `${w.nome} ${w.cognome}`;
    });

    // 4. Combina comunicazioni con recipients
    return (comms || []).map(comm => {
      const commReads = (reads || [])
        .filter(r => r.communication_id === comm.id)
        .map(r => ({
          ...r,
          worker_nome: workerNames[r.worker_id] || 'Sconosciuto'
        }));
      return communicationFromDb(comm, commReads);
    });
  },

  // Crea comunicazione
  async create(comm) {
    const dbData = communicationToDb(comm);
    const { data, error } = await supabase
      .from('communications')
      .insert(dbData)
      .select()
      .single();
    
    if (error) {
      console.error('Errore creazione comunicazione:', error);
      throw error;
    }
    return communicationFromDb(data);
  },

  // Aggiorna comunicazione
  async update(id, comm) {
    const dbData = communicationToDb(comm);
    const { data, error } = await supabase
      .from('communications')
      .update({ ...dbData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore aggiornamento comunicazione:', error);
      throw error;
    }
    return communicationFromDb(data);
  },

  // Elimina comunicazione
  async delete(id) {
    // Prima elimina le letture correlate
    await supabase
      .from('communication_reads')
      .delete()
      .eq('communication_id', id);
    
    const { error } = await supabase
      .from('communications')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Errore eliminazione comunicazione:', error);
      throw error;
    }
    return true;
  },

  // Aggiungi recipients (quando si pubblica una comunicazione)
  async addRecipients(communicationId, workerIds) {
    const records = workerIds.map(workerId => ({
      communication_id: communicationId,
      worker_id: workerId,
      letto: false,
      data_lettura: null,
      solleciti: [],
    }));

    const { data, error } = await supabase
      .from('communication_reads')
      .insert(records)
      .select();
    
    if (error) {
      console.error('Errore aggiunta destinatari:', error);
      throw error;
    }
    return data;
  },

  // Conferma lettura (dal profilo LAVORATORE)
  async confirmRead(communicationId, workerId) {
    const { data, error } = await supabase
      .from('communication_reads')
      .update({ 
        letto: true, 
        data_lettura: new Date().toISOString() 
      })
      .eq('communication_id', communicationId)
      .eq('worker_id', workerId)
      .select()
      .single();
    
    if (error) {
      console.error('Errore conferma lettura:', error);
      throw error;
    }
    return data;
  },

  // Invia sollecito
  async sendReminder(communicationId, workerId, senderName) {
    // Leggi solleciti attuali
    const { data: current, error: readError } = await supabase
      .from('communication_reads')
      .select('solleciti')
      .eq('communication_id', communicationId)
      .eq('worker_id', workerId)
      .single();
    
    if (readError) throw readError;

    const solleciti = current.solleciti || [];
    solleciti.push({
      data: new Date().toISOString(),
      inviato_da: senderName
    });

    const { data, error } = await supabase
      .from('communication_reads')
      .update({ solleciti })
      .eq('communication_id', communicationId)
      .eq('worker_id', workerId)
      .select()
      .single();
    
    if (error) {
      console.error('Errore invio sollecito:', error);
      throw error;
    }
    return data;
  },
};


// ============================================================
// DOCUMENTS - Operazioni CRUD
// ============================================================

export const documentsService = {
  // Leggi tutti i documenti con i recipients
  async getAll() {
    // 1. Leggi documenti
    const { data: docs, error: docError } = await supabase
      .from('documents')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (docError) {
      console.error('Errore lettura documenti:', docError);
      throw docError;
    }

    // 2. Leggi tutte le condivisioni
    const { data: shares, error: shareError } = await supabase
      .from('document_shares')
      .select('*');
    
    if (shareError) {
      console.error('Errore lettura condivisioni:', shareError);
      throw shareError;
    }

    // 3. Leggi nomi lavoratori
    const { data: allWorkers, error: workerError } = await supabase
      .from('workers')
      .select('id, nome, cognome');
    
    if (workerError) throw workerError;

    const workerNames = {};
    (allWorkers || []).forEach(w => {
      workerNames[w.id] = `${w.nome} ${w.cognome}`;
    });

    // 4. Combina documenti con recipients
    return (docs || []).map(doc => {
      const docShares = (shares || [])
        .filter(s => s.document_id === doc.id)
        .map(s => ({
          ...s,
          worker_nome: workerNames[s.worker_id] || 'Sconosciuto'
        }));
      return documentFromDb(doc, docShares);
    });
  },

  // Crea documento
  async create(doc) {
    const dbData = documentToDb(doc);
    const { data, error } = await supabase
      .from('documents')
      .insert(dbData)
      .select()
      .single();
    
    if (error) {
      console.error('Errore creazione documento:', error);
      throw error;
    }
    return documentFromDb(data);
  },

  // Aggiorna documento
  async update(id, doc) {
    const dbData = documentToDb(doc);
    const { data, error } = await supabase
      .from('documents')
      .update({ ...dbData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Errore aggiornamento documento:', error);
      throw error;
    }
    return documentFromDb(data);
  },

  // Elimina documento
  async delete(id) {
    await supabase
      .from('document_shares')
      .delete()
      .eq('document_id', id);
    
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Errore eliminazione documento:', error);
      throw error;
    }
    return true;
  },

  // Condividi documento con lavoratori
  async shareWithWorkers(documentId, workerIds) {
    const records = workerIds.map(workerId => ({
      document_id: documentId,
      worker_id: workerId,
      letto: false,
      data_lettura: null,
      approvato: false,
      data_approvazione: null,
    }));

    const { data, error } = await supabase
      .from('document_shares')
      .insert(records)
      .select();
    
    if (error) {
      console.error('Errore condivisione documento:', error);
      throw error;
    }
    return data;
  },

  // Conferma lettura documento (LAVORATORE)
  async confirmRead(documentId, workerId) {
    const { data, error } = await supabase
      .from('document_shares')
      .update({ 
        letto: true, 
        data_lettura: new Date().toISOString() 
      })
      .eq('document_id', documentId)
      .eq('worker_id', workerId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Invia sollecito documento
  async sendReminder(documentId, workerId, senderName) {
    // Leggi solleciti attuali
    const { data: current, error: readError } = await supabase
      .from('document_shares')
      .select('solleciti')
      .eq('document_id', documentId)
      .eq('worker_id', workerId)
      .single();
    
    if (readError) throw readError;

    const solleciti = current.solleciti || [];
    solleciti.push({
      data: new Date().toISOString(),
      inviato_da: senderName
    });

    const { data, error } = await supabase
      .from('document_shares')
      .update({ solleciti })
      .eq('document_id', documentId)
      .eq('worker_id', workerId)
      .select()
      .single();
    
    if (error) {
      console.error('Errore invio sollecito documento:', error);
      throw error;
    }
    return data;
  },

  // Approva documento (LAVORATORE)
  async approve(documentId, workerId) {
    const { data, error } = await supabase
      .from('document_shares')
      .update({ 
        approvato: true, 
        data_approvazione: new Date().toISOString() 
      })
      .eq('document_id', documentId)
      .eq('worker_id', workerId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};


// ============================================================
// USERS - Gestione utenti
// ============================================================

export const usersService = {
  // Leggi profilo utente corrente
  async getCurrentUser(authId) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', authId)
      .single();
    
    if (error) {
      console.error('Errore lettura profilo utente:', error);
      throw error;
    }
    return data;
  },

  // Crea profilo utente
  async create(userData) {
    const { data, error } = await supabase
      .from('users')
      .insert(userData)
      .select()
      .single();
    
    if (error) {
      console.error('Errore creazione utente:', error);
      throw error;
    }
    return data;
  },
};


// ============================================================
// AUDIT LOG - Registrazione azioni
// ============================================================

export const auditService = {
  async log(action, entityType, entityId, oldValues = null, newValues = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase
        .from('audit_log')
        .insert({
          user_id: user?.id || null,
          user_email: user?.email || 'sistema',
          action,
          entity_type: entityType,
          entity_id: entityId,
          old_values: oldValues,
          new_values: newValues,
        });
    } catch (err) {
      // Non bloccare l'operazione se il log fallisce
      console.error('Errore audit log:', err);
    }
  },
};


// ============================================================
// AUTH - Autenticazione
// ============================================================

export const authService = {
  // Login
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  // Logout
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // Sessione corrente
  async getSession() {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  // Utente corrente
  async getUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  // Ascolta cambiamenti auth
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// ============================
// STORAGE SERVICE (Supabase Storage)
// ============================
export const storageService = {
  // Upload file nel bucket documents
  async upload(filePath, file) {
    const { data, error } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    if (error) throw error;
    return data;
  },

  // Download file (ottieni URL temporaneo firmato)
  async getSignedUrl(filePath, expiresIn = 3600) {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(filePath, expiresIn);
    if (error) throw error;
    return data.signedUrl;
  },

  // Elimina file
  async delete(filePaths) {
    const { data, error } = await supabase.storage
      .from('documents')
      .remove(filePaths);
    if (error) throw error;
    return data;
  },

  // Lista file in una cartella
  async list(folderPath) {
    const { data, error } = await supabase.storage
      .from('documents')
      .list(folderPath);
    if (error) throw error;
    return data;
  }
};

// ============================================================
// EMAIL SERVICE (Resend via Vercel API Route)
// ============================================================
export const emailService = {
  // Invio singola email
  async send({ to, subject, html, from }) {
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, html, from })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Errore invio email');
      }
      
      return data;
    } catch (error) {
      console.error('emailService.send error:', error);
      throw error;
    }
  },

  // Invio multiplo (un'email per destinatario)
  async sendBatch(emails) {
    const results = [];
    for (const email of emails) {
      try {
        const result = await this.send(email);
        results.push({ ...email, success: true, id: result.id });
      } catch (error) {
        results.push({ ...email, success: false, error: error.message });
      }
    }
    return results;
  },

  // Template: Nuova comunicazione
  buildCommunicationEmail({ workerName, communicationTitle, communicationContent, companyName, senderName }) {
    return {
      subject: `SafeTracker - Nuova comunicazione: ${communicationTitle}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #2563eb; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
            <h2 style="margin: 0;">📋 SafeTracker</h2>
            <p style="margin: 5px 0 0; opacity: 0.9;">Nuova comunicazione sulla sicurezza</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
            <p>Gentile <strong>${workerName}</strong>,</p>
            <p>È stata pubblicata una nuova comunicazione per <strong>${companyName}</strong>:</p>
            <div style="background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <p style="margin: 0; font-weight: bold; font-size: 16px;">${communicationTitle}</p>
              ${communicationContent ? `<p style="margin: 10px 0 0; color: #4b5563;">${communicationContent.substring(0, 200)}${communicationContent.length > 200 ? '...' : ''}</p>` : ''}
            </div>
            <p>Accedi a SafeTracker per leggere la comunicazione completa e confermare la presa visione.</p>
            <p style="color: #555555; font-size: 14px; margin-top: 20px; line-height: 1.6;">
              <strong>Inviato da: ${senderName}</strong><br>
              Questa email è stata generata automaticamente da SafeTracker.
              <br>Questa è un'email automatica, si prega di non rispondere a questo indirizzo.
            </p>
          </div>
        </div>
      `
    };
  },

  // Template: Nuovo documento condiviso
  buildDocumentEmail({ workerName, documentDescription, documentType, companyName, senderName }) {
    return {
      subject: `SafeTracker - Nuovo documento condiviso: ${documentDescription}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #059669; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
            <h2 style="margin: 0;">📄 SafeTracker</h2>
            <p style="margin: 5px 0 0; opacity: 0.9;">Nuovo documento condiviso</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
            <p>Gentile <strong>${workerName}</strong>,</p>
            <p>È stato condiviso un nuovo documento per <strong>${companyName}</strong>:</p>
            <div style="background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <p style="margin: 0; font-weight: bold;">${documentType}: ${documentDescription}</p>
            </div>
            <p>Accedi a SafeTracker per consultare il documento e confermare la presa visione.</p>
            <p style="color: #555555; font-size: 14px; margin-top: 20px; line-height: 1.6;">
              <strong>Inviato da: ${senderName}</strong><br>
              Questa email è stata generata automaticamente da SafeTracker.
              <br>Questa è un'email automatica, si prega di non rispondere a questo indirizzo.
            </p>
          </div>
        </div>
      `
    };
  },

  // Template: Sollecito lettura
  buildReminderEmail({ workerName, itemTitle, itemType, companyName, senderName, reminderNumber }) {
    return {
      subject: `SafeTracker - Sollecito: ${itemTitle}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #d97706; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
            <h2 style="margin: 0;">⏰ SafeTracker</h2>
            <p style="margin: 5px 0 0; opacity: 0.9;">Sollecito lettura N° ${reminderNumber}</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
            <p>Gentile <strong>${workerName}</strong>,</p>
            <p>Le ricordiamo che è necessario prendere visione della seguente ${itemType === 'comunicazione' ? 'comunicazione' : 'documento'} per <strong>${companyName}</strong>:</p>
            <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #d97706;">
              <p style="margin: 0; font-weight: bold;">${itemTitle}</p>
            </div>
            <p>Accedi a SafeTracker per completare la presa visione.</p>
            <p style="color: #555555; font-size: 14px; margin-top: 20px; line-height: 1.6;">
              <strong>Inviato da: ${senderName}</strong><br>
              Sollecito N° ${reminderNumber}<br>
              Questa email è stata generata automaticamente da SafeTracker.
              <br>Questa è un'email automatica, si prega di non rispondere a questo indirizzo.
            </p>
          </div>
        </div>
      `
    };
  },

  // Template: Alert scadenza contratto
  buildContractExpiryEmail({ datoreName, companyName, expiryDate, daysLeft, urgencyLevel }) {
    const colors = {
      attenzione: '#3b82f6',
      in_scadenza: '#f59e0b', 
      scaduto: '#ef4444'
    };
    const titles = {
      attenzione: 'Promemoria scadenza contratto',
      in_scadenza: 'URGENTE: Contratto in scadenza',
      scaduto: 'Contratto scaduto'
    };
    
    return {
      subject: `SafeTracker - ${titles[urgencyLevel]}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: ${colors[urgencyLevel]}; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
            <h2 style="margin: 0;">⚠️ SafeTracker</h2>
            <p style="margin: 5px 0 0; opacity: 0.9;">${titles[urgencyLevel]}</p>
          </div>
          <div style="padding: 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
            <p>Gentile <strong>${datoreName}</strong>,</p>
            ${urgencyLevel === 'attenzione' ? 
              `<p>Le ricordiamo che il contratto SafeTracker per <strong>${companyName}</strong> scadrà tra <strong>${daysLeft} giorni</strong> (${expiryDate}).</p>
               <p>La invitiamo a contattarci per discutere il rinnovo.</p>` :
            urgencyLevel === 'in_scadenza' ? 
              `<p><strong>ATTENZIONE:</strong> Il contratto SafeTracker per <strong>${companyName}</strong> scadrà tra soli <strong>${daysLeft} giorni</strong> (${expiryDate}).</p>
               <p>Senza rinnovo, l'accesso alla piattaforma verrà disabilitato alla scadenza.</p>
               <p>La preghiamo di contattarci urgentemente.</p>` :
              `<p>La informiamo che il contratto SafeTracker per <strong>${companyName}</strong> è <strong>scaduto</strong> in data ${expiryDate}.</p>
               <p>L'accesso alla piattaforma è stato disabilitato.</p>
               <p>Per riattivare il servizio, La preghiamo di contattarci.</p>`
            }
            <p style="color: #555555; font-size: 14px; margin-top: 20px; line-height: 1.6;">
              Questa email è stata generata automaticamente da SafeTracker.
              <br>Questa è un'email automatica, si prega di non rispondere a questo indirizzo.
            </p>
          </div>
        </div>
      `
    };
  }
};


// ============================================================
// AUDIT LOG SERVICE - Registrazione azioni per tracciabilità legale
// ============================================================
// Ogni azione critica viene registrata nella tabella audit_log.
// Il log è IMMUTABILE: solo INSERT, mai UPDATE o DELETE.
// ============================================================

export const auditLogService = {
  /**
   * Registra un evento nel log di audit
   * @param {string} action - Tipo azione: 'CREATE', 'READ', 'UPDATE', 'DELETE', 'SHARE', 'CONFIRM', 'LOGIN', 'EXPORT'
   * @param {string} entityType - Tipo entità: 'communication', 'document', 'company', 'worker', 'document_share', 'communication_read'
   * @param {string|null} entityId - ID dell'entità coinvolta
   * @param {object} options - Opzioni aggiuntive
   * @param {string} options.userEmail - Email dell'utente che compie l'azione
   * @param {object} options.oldValues - Valori precedenti (per UPDATE)
   * @param {object} options.newValues - Nuovi valori o dettagli azione
   */
  async log(action, entityType, entityId, options = {}) {
    try {
      const record = {
        action: action,
        entity_type: entityType,
        entity_id: entityId || null,
        user_email: options.userEmail || null,
        old_values: options.oldValues || null,
        new_values: options.newValues || null,
        ip_address: null,
        user_agent: navigator.userAgent || null,
      };

      const { data, error } = await supabase
        .from('audit_log')
        .insert(record)
        .select()
        .single();

      if (error) {
        console.error('Errore audit log:', error);
        // Non lanciamo l'errore: il log non deve bloccare l'operazione principale
        return null;
      }

      console.log(`[AUDIT] ${action} ${entityType} ${entityId || ''}`);
      return data;
    } catch (err) {
      // Il log audit non deve MAI bloccare l'app
      console.error('Errore audit log (catch):', err);
      return null;
    }
  },

  // Leggi tutti i log (per pannello ADMIN)
  async getAll(limit = 100) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Errore lettura audit log:', error);
      throw error;
    }
    return data || [];
  },

  // Leggi log per una specifica entità
  async getByEntity(entityType, entityId) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Errore lettura audit log entità:', error);
      throw error;
    }
    return data || [];
  },

  // Leggi log per un utente specifico
  async getByUser(userEmail, limit = 50) {
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Errore lettura audit log utente:', error);
      throw error;
    }
    return data || [];
  },

  // Conta totale eventi (per statistiche ADMIN)
  async getStats() {
    const { data, error } = await supabase
      .from('audit_log')
      .select('action, entity_type');

    if (error) {
      console.error('Errore statistiche audit log:', error);
      return { total: 0, byAction: {}, byEntity: {} };
    }

    const stats = {
      total: data.length,
      byAction: {},
      byEntity: {},
    };

    data.forEach(row => {
      stats.byAction[row.action] = (stats.byAction[row.action] || 0) + 1;
      stats.byEntity[row.entity_type] = (stats.byEntity[row.entity_type] || 0) + 1;
    });

    return stats;
  }
};
